// src/App.js
import ImageUploader from './screens/ImageUploader';
import JobInfoReviewer from './screens/JobInfoReviewer';
import InvoiceGenerator from './screens/InvoiceGenerator';
import CreateInvoice from './screens/CreateInvoice';
import EditTickets from './screens/EditTickets';
import InvoiceManager from './screens/InvoiceManager';
import Ticket from './screens/Ticket';
import WeeklyInvoice from './screens/WeeklyInvoice';
import WeeklySummary from './screens/WeeklySummary';
import WeeklyRecon from './screens/WeeklyRecon';
import TicketReport from './screens/TicketReport';
import TicketIntake from './screens/TicketIntake';
import Invoicing from './screens/Invoicing';
import Payroll from './screens/Payroll';
import IvyInvoice from './screens/IvyInvoice';
import IvyPlantInvoice from './screens/IvyPlantInvoice';
import FlatbedInvoice from './screens/FlatbedInvoice';
import FieldTickets from './screens/FieldTickets';
import FieldTicketInvoice from './screens/FieldTicketInvoice';
import FuelExpenses from './screens/FuelExpenses';
import Reconciliation from './screens/Reconciliation';
import VerifyWorkspace from './screens/VerifyWorkspace';
import Drivers from './screens/masters/Drivers';
import Carriers from './screens/masters/Carriers';
import Trucks from './screens/masters/Trucks';
import Plants from './screens/masters/Plants';
import Companies from './screens/masters/Companies';
import Vendors from './screens/masters/Vendors';
import RateCards from './screens/masters/RateCards';
import PlantPaymentConfigs from './screens/masters/PlantPaymentConfigs';
import HauledForRules from './screens/masters/HauledForRules';
import OcrTool from './screens/admin/OcrTool';
import Users from './screens/admin/Users';
import Onboarding from './screens/Onboarding';
import Billing from './screens/Billing';
import BillingBanner from './screens/components/BillingBanner';

import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import NavigationMenu from './screens/NavigationMenu';
import SignUp from './screens/SignUp';
import Login from './screens/Login';
import ProtectedRoute from './screens/ProtectedRoute';
import { installIdTokenInterceptor } from './auth/attachIdToken';
import RoleNeeded from './screens/RoleNeeded';
import { RoleProvider, useRole } from './auth/RoleContext';
import { isConfiguredRole } from './auth/permissions';
import { ConfirmProvider } from './screens/components/ConfirmProvider';
import { NotificationProvider } from './screens/components/NotificationProvider';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './screens/firebaseConfig';
import Home from './screens/Home';
import Logout from './screens/Logout';
import { LanguageProvider } from './i18n/LanguageContext';
import axios from 'axios';

// App-wide theme: default every input to the compact `small` size so all form/filter fields share
// one height across screens (previously some screens used MUI's default `medium`, which read as
// oversized next to the standardized Filters bar). Fields that pass an explicit size still win.
// On phones every button is at least 44px tall (Apple's minimum touch target) — gloved, one-handed
// use in a truck cab misses the desktop-sized ones.
const PHONE = '@media (max-width:899.95px)';
const theme = createTheme({
  components: {
    MuiTextField: { defaultProps: { size: 'small' } },
    MuiFormControl: { defaultProps: { size: 'small' } },
    MuiSelect: { defaultProps: { size: 'small' } },
    MuiAutocomplete: { defaultProps: { size: 'small' } },
    MuiButton: { styleOverrides: { root: { [PHONE]: { minHeight: 44 } } } },
    MuiIconButton: { styleOverrides: { root: { [PHONE]: { minWidth: 44, minHeight: 44 } } } },
  },
});

// Routes that must never show the app chrome (top nav), even if a Firebase session exists.
const AUTH_ROUTES = ['/login', '/signup', '/logout'];

function AppChrome() {
    const location = useLocation();
    const [user] = useAuthState(auth);
    const { role, loadingRole, needsOnboarding } = useRole();

    const onAuthRoute = AUTH_ROUTES.includes(location.pathname);
    const authedApp = !!user && !onAuthRoute;
    const configured = isConfiguredRole(role);

    // Signed in and still resolving the role — show a spinner instead of an empty menu bar.
    if (authedApp && loadingRole) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <CircularProgress size={30} />
            </Box>
        );
    }

    // Signed in but no configured role: a brand-new customer sets up their own account; anyone else
    // (e.g. invited but not yet given a role) is asked to have it configured.
    if (authedApp && !configured) {
        return needsOnboarding ? <Onboarding /> : <RoleNeeded email={user?.email} />;
    }

    return (
        <>
            {authedApp && configured && <NavigationMenu />}
            {authedApp && configured && <BillingBanner />}
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/logout" element={<Logout />} />

                <Route path="/ScanTicket" element={<ProtectedRoute><ImageUploader /></ProtectedRoute>} />
                <Route path="/Ticket" element={<ProtectedRoute><Ticket/></ProtectedRoute>} />
                {/* <Route path="/ViewTicket" element={<ProtectedRoute><InvoiceGenerator /></ProtectedRoute>} /> */}
                <Route path="/CreateInvoice" element={<ProtectedRoute><CreateInvoice /></ProtectedRoute>} />
                {/* <Route path="/EditTicket" element={<ProtectedRoute><InvoiceManager /></ProtectedRoute>} /> */}
                {/* <Route path="/AddTicket" element={<ProtectedRoute><AddTicket /></ProtectedRoute>} /> */}

                <Route path="/invoices/weekly" element={<ProtectedRoute><WeeklyInvoice /></ProtectedRoute>} />
                <Route path="/WeeklySummary" element={<ProtectedRoute><WeeklySummary /></ProtectedRoute>} />
                <Route path="/WeeklyRecon" element={<ProtectedRoute><WeeklyRecon /></ProtectedRoute>} />
                <Route path="/TicketReport" element={<ProtectedRoute><TicketReport /></ProtectedRoute>} />
                <Route path="/TicketIntake" element={<ProtectedRoute><TicketIntake /></ProtectedRoute>} />
                {/* Combined entry point. /CreateInvoice and /IvyInvoice stay routed: existing
                    bookmarks, and the role lists still name them. */}
                <Route path="/Invoicing" element={<ProtectedRoute><Invoicing /></ProtectedRoute>} />
                <Route path="/RevenueSummary" element={<ProtectedRoute><WeeklySummary /></ProtectedRoute>} />
                <Route path="/Payroll" element={<ProtectedRoute><Payroll /></ProtectedRoute>} />
                <Route path="/IvyInvoice" element={<ProtectedRoute><IvyInvoice /></ProtectedRoute>} />
                <Route path="/IvyPlantInvoice" element={<ProtectedRoute><IvyPlantInvoice /></ProtectedRoute>} />
                <Route path="/FlatbedInvoice" element={<ProtectedRoute><FlatbedInvoice /></ProtectedRoute>} />
                <Route path="/FieldTickets" element={<ProtectedRoute><FieldTickets /></ProtectedRoute>} />
                <Route path="/FieldTicketInvoice" element={<ProtectedRoute><FieldTicketInvoice /></ProtectedRoute>} />
                <Route path="/FuelExpenses" element={<ProtectedRoute><FuelExpenses /></ProtectedRoute>} />
                <Route path="/Reconciliation" element={<ProtectedRoute><Reconciliation /></ProtectedRoute>} />
                <Route path="/Verify" element={<ProtectedRoute><VerifyWorkspace /></ProtectedRoute>} />

                <Route path="/Masters/Drivers" element={<ProtectedRoute><Drivers /></ProtectedRoute>} />
                <Route path="/Masters/Carriers" element={<ProtectedRoute><Carriers /></ProtectedRoute>} />
                <Route path="/Masters/Trucks" element={<ProtectedRoute><Trucks /></ProtectedRoute>} />
                <Route path="/Masters/Plants" element={<ProtectedRoute><Plants /></ProtectedRoute>} />
                <Route path="/Masters/Companies" element={<ProtectedRoute><Companies /></ProtectedRoute>} />
                <Route path="/Masters/Vendors" element={<ProtectedRoute><Vendors /></ProtectedRoute>} />
                <Route path="/Masters/RateCards" element={<ProtectedRoute><RateCards /></ProtectedRoute>} />
                <Route path="/Masters/PlantPaymentConfigs" element={<ProtectedRoute><PlantPaymentConfigs /></ProtectedRoute>} />
                <Route path="/Masters/HauledForRules" element={<ProtectedRoute><HauledForRules /></ProtectedRoute>} />
                <Route path="/Admin/Ocr" element={<ProtectedRoute><OcrTool /></ProtectedRoute>} />
                <Route path="/Admin/Users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
                <Route path="/Billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
            </Routes>
        </>
    );
}

function App() {
    axios.defaults.headers.common['ngrok-skip-browser-warning'] = "true";
  // Attach the Firebase ID token to every call to our own API. Installed once, at the top of the
  // tree, so no screen has to remember to authenticate — 33 files call axios directly.
  installIdTokenInterceptor();
    const [, loading] = useAuthState(auth);

    if (loading) {
        return <div>Loading...</div>;  // Show a loading screen while authentication state is being checked
    }

    return (
        <ThemeProvider theme={theme}>
            <LanguageProvider>
                <Router>
                    <RoleProvider>
                        <NotificationProvider>
                            <ConfirmProvider>
                                <AppChrome />
                            </ConfirmProvider>
                        </NotificationProvider>
                    </RoleProvider>
                </Router>
            </LanguageProvider>
        </ThemeProvider>
    );
}

export default App;
